import { useMemo, useState } from "react";
import { ArrowLeft, MapPin, Building2 } from "lucide-react";
import { useParams } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PropertyFilters from "@/components/PropertyFilters";
import PropertyListingCard from "@/components/PropertyListingCard";
import { useLocationBySlug } from "@/hooks/useLocations";
import { useProperties } from "@/hooks/useProperties";
import { applyPropertySearchFilters, defaultPropertySearchFilters, type PropertySearchFilters } from "@/lib/propertyFilters";

const LocationDetailsPage = () => {
  const { slug } = useParams();
  const [filters, setFilters] = useState<PropertySearchFilters>(defaultPropertySearchFilters);
  const { data: location, isLoading: loadingLocation } = useLocationBySlug(slug);
  const { data: properties, isLoading: loadingProperties } = useProperties({
    location_id: location?.id,
    enabled: Boolean(location?.id),
  });
  const filteredProperties = useMemo(() => applyPropertySearchFilters(properties ?? [], filters), [properties, filters]);

  return (
    <div className="bg-brand-canvas min-h-screen">
      <Header />
      <main className="pt-36 md:pt-40 w-full">
        {location && (
          <div className="px-4 md:px-16 py-16">
            <div className="max-w-7xl mx-auto">
              <div className="mb-10">
                <h2 className="font-display text-3xl md:text-4xl font-light text-primary-foreground mb-3">Properties in {location.name}</h2>
                <p className="font-body text-primary-foreground/75">Every property below is linked to this location through database relationship.</p>
              </div>

              <PropertyFilters
                value={filters}
                locations={[]}
                onChange={setFilters}
                onReset={() => setFilters(defaultPropertySearchFilters)}
                showLocation={false}
                className="mb-8"
              />

              <p className="font-body text-sm text-primary-foreground/75 mb-6">
                Showing <span className="text-primary-foreground font-semibold">{filteredProperties.length}</span> properties in {location.name}.
              </p>

              {loadingProperties ? (
                <p className="text-center text-primary-foreground/70 font-body">Loading properties...</p>
              ) : filteredProperties.length > 0 ? (
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {filteredProperties.map((property) => (
                    <PropertyListingCard key={property.id} {...property} statusLabel={property.status_label} categoryLabel={property.category_label} />
                  ))}
                </div>
              ) : (
                <p className="text-center text-primary-foreground/70 font-body py-12">No properties in this location match the selected filters.</p>
              )}
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default LocationDetailsPage;